import React, { useCallback, useMemo, useState } from "react";

import {
  FeatureFlags,
  FMEARiskList as FMEARiskListType,
  isRiskPolicy,
} from "@design-controls/types";
import {
  createQColumnHelper,
  DataProvider,
  Filtering,
  Pagination,
  QDataTable,
  QDeleteConfirmationModal,
  QText,
  TableMenuItem,
  useFeatureFlag,
} from "@qualio/ui-components";
import { Outlet, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { usePolicyGroupResourceQuickSearch } from "../../hooks/search/usePolicyGroupResourceQuickSearch";
import { CategoryFilter } from "../../filter/CategoryFilter";
import { useDeleteRisk } from "../../hooks/designElement/useDeleteRisk";
import { useFMEARiskList } from "../../hooks/designElement/useFMEARiskList";
import { GapTableCell } from "../../components/GapTableCell/GapTableCell";
import { ShowOnlyOpen } from "../../filter/ShowOnlyOpen";
import { designElementStatusColumn } from "../../components/DesignElementStatusColumn/DesignElementStatusColumn";
import { designElementVersionColumn } from "../../components/DesignElementVersionColumn/DesignElementVersionColumn";
import { qriColumn } from "../../components/QRIColumn/QRIColumn";
import { filterByStatus, StatusFilter } from "../../filter/StatusFilter";
import useHasPermission from "../../hooks/useHasPermission";
import { UserPermissions } from "../../domain/permissions";
import { Link } from "react-router-dom";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";

const columnHelper = createQColumnHelper<FMEARiskListType>();

export const FMEARiskList = () => {
  const { t } = useTranslation();
  const versioningEnabled = Boolean(
    useFeatureFlag()[FeatureFlags.MAJOR_MINOR_VERSION],
  );
  const { isLoading, data } = useFMEARiskList();
  const { id: productId } = useCurrentProduct();
  const [itemToDelete, setItemToDelete] = useState<FMEARiskListType | null>(
    null,
  );
  const { isLoading: isDeleting, mutate: deleteElement } = useDeleteRisk();
  const navigate = useNavigate();
  const permissions = useHasPermission([
    UserPermissions.EDIT_DESIGN_ELEMENTS,
    UserPermissions.DELETE_DESIGN_ELEMENTS,
  ]);

  const { isLoading: categoryIsLoading, data: categories } =
    usePolicyGroupResourceQuickSearch(isRiskPolicy);

  const filterDefinitions = useMemo(() => {
    return {
      status: {
        label: t("risks.list.filters.status"),
        schema: Filtering.schemas.StringSchema().array().nullish(),
      },
      category: {
        label: t("risks.list.filters.category"),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: (v: any) => {
          const item = categories?.find((c) => c.identifiers.qri === v);
          if (!item) {
            return;
          }

          return {
            label: t("risks.list.filterLabels.category"),
            value: item.displayLabel,
          };
        },
      },
      show_unmitigated: {
        label: t("risks.list.filters.showUnmitigated"),
        schema: Filtering.schemas.BooleanSchema().nullish(),
        activeRender: (v: any) => ({
          label: null,
          value: v && t("risks.list.filterLabels.unmitigated"),
        }),
      },
    };
  }, [categories, t]);

  const filterOption = useCallback(
    (
      item: Readonly<FMEARiskListType>,
      searchTerm: string | undefined,
      filters: Filtering.ResolvedFilters<typeof filterDefinitions> | undefined,
    ) => {
      if (
        searchTerm &&
        !item.title
          .toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) &&
        !item.category?.label
          .toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) &&
        !item.code.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
      ) {
        return false;
      }

      if (filters && Object.values(filters).some((field) => field.value)) {
        const { category, status, show_unmitigated } = filters;

        return (
          (!show_unmitigated?.value || item.policyIssues.UNMITIGATED) &&
          (!status?.value?.length || filterByStatus(item, status.value)) &&
          (!category?.value?.length ||
            category.value.includes(item.category?.qri))
        );
      }

      return true;
    },
    [],
  ) as DataProvider.FilterOptionFn<FMEARiskListType>;

  const columns = useMemo(() => {
    return [
      columnHelper.code("code", {
        header: t("risks.list.table.header.id"),
        maxWidth: "100px",
      }),
      {
        header: " ",
        meta: {
          width: "40px",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: FMEARiskListType };
        }) => <GapTableCell openIssues={row.policyIssues} type={row.type} />,
      },
      columnHelper.custom("title", {
        header: t("risks.list.table.header.riskName"),
        maxWidth: "400px",
        enableSorting: true,
        cell: (props) => {
          return <Link to={props.row.id}>{props.getValue()}</Link>;
        },
      }),
      qriColumn(
        "category",
        (record) => record.category?.label,
        t("risks.list.table.header.category"),
      ),
      columnHelper.date((record) => new Date(record.updated), {
        header: t("risks.list.table.header.lastModified"),
        id: "updated",
        maxWidth: "150px",
      }),
      ...(versioningEnabled
        ? [designElementVersionColumn(t("common.metadata.version"))]
        : []),
      designElementStatusColumn(),
      columnHelper.menu({
        items: (
          <>
            <TableMenuItem
              onClick={(item: FMEARiskListType) => navigate(`${item.id}/edit`)}
              data-cy="edit"
              isDisabled={(item: FMEARiskListType) =>
                !["product-development", "qualio"].includes(item.source)
              }
              isHidden={() =>
                !permissions[UserPermissions.EDIT_DESIGN_ELEMENTS]
              }
            >
              {t("risks.list.actions.edit")}
            </TableMenuItem>
            <TableMenuItem
              onClick={(item: FMEARiskListType) => setItemToDelete(item)}
              color="red.500"
              data-cy="delete"
              isHidden={() =>
                !permissions[UserPermissions.DELETE_DESIGN_ELEMENTS]
              }
            >
              {t("risks.list.actions.delete")}
            </TableMenuItem>
          </>
        ),
        hide: (record) => record.source !== "product-development",
      }),
    ];
  }, [permissions, t, navigate, versioningEnabled]);

  return (
    <>
      <Pagination.Auto pageSizeKey="size" pageIndexKey="page" clientSide>
        <Filtering.FilterProvider
          definitions={filterDefinitions}
          searchTermKey="search"
          persistanceKey={`fmea-risk-list-filters-${productId}`}
        >
          <DataProvider.Fixed
            data={data ?? []}
            isLoading={isLoading}
            filterOption={filterOption}
          >
            <QDataTable
              columns={columns}
              data-cy="fmea-risk-list"
              getRowId={(row: FMEARiskListType) => row.id}
            >
              <Filtering.FormContent>
                <CategoryFilter
                  categories={categories}
                  isLoading={categoryIsLoading}
                />
                <StatusFilter />
                <ShowOnlyOpen type="risk" />
              </Filtering.FormContent>
            </QDataTable>
          </DataProvider.Fixed>
        </Filtering.FilterProvider>
      </Pagination.Auto>
      <Outlet />
      {itemToDelete && (
        <QDeleteConfirmationModal
          title={t("risks.delete.modalTitle")}
          onConfirm={() => {
            itemToDelete &&
              deleteElement(itemToDelete as any, {
                onSettled: () => setItemToDelete(null),
              });
          }}
          inProgress={isDeleting}
          onClose={() => setItemToDelete(null)}
          message={
            <QText fontSize="sm">
              {t("risks.delete.confirmMessage", {
                riskCodeTitle: itemToDelete.codeTitle,
              })}
            </QText>
          }
        />
      )}
    </>
  );
};
